import { isNullOrUndefined } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { MenuItem } from '@adaptiva/core/menus';
import { OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, switchMap } from 'rxjs';
import { LlmConversationNode } from '../classes';
import { ILlmPayload } from '../interfaces';
import { LlmForkService } from '../services';
import { BaseLlmNodeMenuController } from './base-llm-node-menu-controller';

@Injectable()
export class LlmForkMenuController extends BaseLlmNodeMenuController {

	public readonly renameMenuName: string = 'Rename';

	public readonly deleteMenuName: string = 'Delete';

	protected readonly forks: LlmForkService = inject(LlmForkService);

	protected readonly overlays: OverlayService = inject(OverlayService);

	protected readonly snackbars: SnackbarService = inject(SnackbarService);

	protected readonly logging: LoggingService = inject(LoggingService);

	public override generate(_node: LlmConversationNode<ILlmPayload>): Observable<MenuItem<LlmConversationNode<ILlmPayload>>[]> {
		return of([
			this.getOpenInChatMenuItem(_node),
			this.getCreateForkMenuItem(_node),
			this.getOpenInHistoryMenuItem(_node),
			this.getRenameMenuItem(_node),
			this.getDeleteMenuItem(_node),
		]);
	}

	public getRenameMenuItem(_node: LlmConversationNode): MenuItem<LlmConversationNode> {
		const _itemId: string = this.hashItem(this.renameMenuName, _node);
		const _item: MenuItem<LlmConversationNode> = this.getItem(_itemId, _node) || this.createItem({
			id: _itemId,
			name: this.renameMenuName,
			data: _node,
			onSelect: this.rename.bind(this, _node)
		});

		return _item;
	}

	public getDeleteMenuItem(_node: LlmConversationNode): MenuItem<LlmConversationNode> {
		const _itemId: string = this.hashItem(this.deleteMenuName, _node);
		const _item: MenuItem<LlmConversationNode> = this.getItem(_itemId, _node) || this.createItem({
			id: _itemId,
			name: this.deleteMenuName,
			data: _node,
			onSelect: this.delete.bind(this, _node),
			isDisabled: new BehaviorSubject<boolean>(this.aida.conversationTree()?.forkName === _node.forkName)
		});

		return _item;
	}

	protected rename(_fork: LlmConversationNode): void {
		const _activeConversationId: long = this.aida.conversation()?.objectId;

		if (!_activeConversationId)
			return;

		this.overlays.prompt<string>({
			label: 'Rename Fork',
			title: `Rename ${_fork.forkName}`,
			// TODO: Prevent duplicate fork names
			validators: [(_name: string) => (_name !== _fork.forkName && !isNullOrUndefined(_name))],
			type: 'text',
		}).pipe(
			this.takeOneUntilDestroyed(),
			switchMap((_newName: string) => {
				return this.forks.renameFork(
					{
						conversationId: _activeConversationId,
						newForkName: _newName,
						oldForkName: _fork.forkName
					},
					_fork.nodeId
				);
			})
		).subscribe({
			next: () => {
				this.snackbars.display('Renamed Fork');
			},
			error: (e: Error) => {
				this.logging.defaultErrorHandler('ForkMenuController.rename', e);
			}
		});
	}

	protected delete(_fork: LlmConversationNode): void {
		const _conversationId: long = this.aida.conversation()?.objectId;

		if (!_conversationId)
			return;

		this.overlays.confirm(
			'Are you sure?',
			`Are you sure you want to delete ${_fork.forkName}?\nThis action cannot be undone.`
		).pipe(
			this.takeOneUntilDestroyed(),
			switchMap((_isConfirmed: boolean) => {
				if (!_isConfirmed)
					return EMPTY;

				return this.forks.deleteFork(_fork);
			}),
		).subscribe({
			next: () => {
				this.snackbars.display(`Successfully deleted ${_fork.forkName}`);
			},
			error: (_error: Error) => {
				this.logging.defaultErrorHandler('ForkMenuController.delete', _error);
			}
		});
	}

}
